import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { GusQueryState, GusQueryType } from 'src/lib/fliko/models/integrations/gusQuery.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { GusDataSimple } from '../gus.data';
import { GusQueryService } from '../gus-query.service';

@Component({
  selector: 'app-gus-autofill-button',
  templateUrl: './gus-autofill-button.component.html',
  styleUrls: ['./gus-autofill-button.component.scss']
})
export class GusAutofillButtonComponent implements AfterViewInit, OnDestroy {
  @Input() nip?: string;
  @Input() disabled: boolean = false;
  @Input() margin: boolean = true;

  @Output() resolveGusData = new EventEmitter<GusDataSimple>();

  public querySent: boolean = false;

  private userId?: string;

  private anyQuerySent = false;
  private subscription$ = new Subscription();

  constructor(
    private gusService: GusQueryService,
    private errorHandlingService: ErrorHandlingService,
    private ctxDS: ContextDataService,
    private ls: LocalizationService
  ) {}

  ngAfterViewInit(): void {
    this.subscription$.add(
      this.ctxDS.currentUser$
        .pipe(
          switchMap((user) => {
            this.userId = user?.id;
            return this.userId ? this.gusService.getGusQuery$() : of(null);
          })
        )
        .subscribe((query) => {
          if (query) {
            // HACK: sometimes, gusQuery is not removed (afer invalid closure of component), so we need to remove it to omit
            // garbage data returned to form
            if (!this.nip && !!query.query.param) {
              this.gusService.sendLogoutQueryToGus();
              return;
            }
            switch (query.state) {
              case GusQueryState.LOGGED_IN: {
                if (query.query.type == GusQueryType.LOGOUT) break;
                const response = query.nipResponse;
                if (response) {
                  let number = response.buildingNumber ?? '';
                  if (response.premisesNumber) {
                    number += `/${response.premisesNumber}`;
                  }
                  const dataToEmit: GusDataSimple = {
                    city: response.city,
                    country: 'Polska',
                    name: response.name,
                    number,
                    REGON: response.regon,
                    street: this.sanitizeStreet(response.street),
                    zipCode: response.zipCode
                  };
                  this.resolveGusData.emit(dataToEmit);
                }
                break;
              }
              case GusQueryState.CONNECTION_ERROR: {
                this.errorHandlingService.showGlobalError(this.ls.translate('error.GUSError'));
                break;
              }
              case GusQueryState.DATA_ERROR: {
                this.errorHandlingService.showGlobalError(this.ls.translate('error.NIPNotFound'));
                break;
              }
              default:
                break;
            }
            this.querySent = false;
          }
        })
    );
  }

  ngOnDestroy(): void {
    if (this.userId && this.anyQuerySent) {
      this.gusService.sendLogoutQueryToGus();
    }
    this.subscription$.unsubscribe();
  }

  public async onClick(): Promise<void> {
    if (this.userId ?? this.nip) {
      await this.gusService.sendNipQueryToGus(this.userId!, ParserHlp.sanitizeNumber(this.nip, 10));
      this.querySent = true;
      this.anyQuerySent = true;
    }
  }

  private sanitizeStreet(street: string): string {
    return street.replace('ul.', '').trim();
  }
}
